import React, { useState } from 'react';
import injectSheet from 'react-jss';

import { EM_DASH } from '~/common/constants/unicode';
import { Button } from '~/eds/src/components/Button/Button';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { useIntl } from 'react-intl';
import { MODAL_SIMPLE } from '~/types/modal.types';

import {
  black2,
  evisortBlue,
  evisortOrange,
  evisortRed,
  evisortYellow,
} from '../../../assets/shared-styles/general';
import { algorithmStatusHelper } from '../../../utils/helper.utils';
import { featureFlagIncluded, testIsSuperAdmin } from '../../../utils/user';
import EcModal from '../../Shared/EcModal/EcModal';
import CheckmarkIcon from '../../Shared/Icons/CheckmarkIcon';
import CloseIcon from '../../Shared/Icons/CloseIcon';
import InfoIcon from '../../Shared/Icons/InfoIcon';
import QueueIcon from '../../Shared/Icons/QueueIcon';
import SyncIcon from '../../Shared/Icons/SyncIcon';
import UploadArrowUpIcon from '../../Shared/Icons/UploadArrowUpIcon';
import WarningIcon from '../../Shared/Icons/WarningIcon';
import styles from './UploadStatus.styles';

function UploadStatus({ classes, status, progress, algoStatus, user, logId }) {
  const intl = useIntl();
  const [modal, showModal] = useState();

  const UploadStatusTooltip = {
    COMPLETED: intl.formatMessage({
      id: 'UploadStatus.completedTooltip',
      defaultMessage: 'Your document was uploaded and processed successfully',
      description: 'Tooltip for completed upload status',
    }),
    QUEUED: intl.formatMessage({
      id: 'UploadStatus.queuedTooltip',
      defaultMessage:
        'Your document was uploaded successfully and has been placed in line for processing, which may take several minutes',
      description: 'Tooltip for queued upload status',
    }),
    PROCESSING: intl.formatMessage({
      id: 'UploadStatus.processingTooltip',
      defaultMessage:
        'Your document is being processed and should be available shortly',
      description: 'Tooltip for processing upload status',
    }),
    UPLOAD_FAILED: intl.formatMessage({
      id: 'UploadStatus.uploadFailedTooltip',
      defaultMessage:
        'Your document was not processed due to a system error. Please try uploading again later or contact support.',
      description: 'Tooltip for upload failed status',
    }),
    UPLOAD_FILETYPE_FAILURE: intl.formatMessage({
      id: 'UploadStatus.uploadFiletypeFailureTooltip',
      defaultMessage:
        'Your document was not uploaded because its file type is not supported.',
      description: 'Tooltip for unsupported file type status',
    }),
    UPLOADING: intl.formatMessage({
      id: 'UploadStatus.uploadingTooltip',
      defaultMessage: 'Your document is uploading',
      description: 'Tooltip for uploading status',
    }),
    OCR_FAILURE: intl.formatMessage({
      id: 'UploadStatus.ocrFailureTooltip',
      defaultMessage:
        'Your document was not processed due to a system error. Please try uploading again later or contact support.',
      description: 'Tooltip for OCR failure status',
    }),
    ALGORITHM_FAILURE: intl.formatMessage({
      id: 'UploadStatus.algorithmFailureTooltip',
      defaultMessage:
        'Your document was not processed due to a system error. Please try uploading again later or contact support.',
      description: 'Tooltip for algorithm failure status',
    }),
  };

  const UploadStatuses = {
    COMPLETED: intl.formatMessage({
      id: 'UploadStatus.completedStatus',
      defaultMessage: 'Completed',
      description: 'Completed upload status text',
    }),
    QUEUED: intl.formatMessage({
      id: 'UploadStatus.queuedStatus',
      defaultMessage: 'Queued',
      description: 'Queued upload status text',
    }),
    PROCESSING: intl.formatMessage({
      id: 'UploadStatus.processingStatus',
      defaultMessage: 'Processing',
      description: 'Processing upload status text',
    }),
    PROCESSING_ERROR: intl.formatMessage({
      id: 'UploadStatus.processingErrorStatus',
      defaultMessage: 'Processing Error',
      description: 'Processing error status text',
    }),
    UPLOAD_FAILED: intl.formatMessage({
      id: 'UploadStatus.uploadFailedStatus',
      defaultMessage: 'Upload Failed',
      description: 'Upload failed status text',
    }),
    UPLOAD_FILETYPE_FAILURE: intl.formatMessage({
      id: 'UploadStatus.uploadFiletypeFailureStatus',
      defaultMessage: 'Upload Failed',
      description: 'Upload filetype failure status text',
    }),
    UPLOADING: intl.formatMessage({
      id: 'UploadStatus.uploadingStatus',
      defaultMessage: 'Uploading',
      description: 'Uploading status text',
    }),
    OCR_FAILURE: intl.formatMessage({
      id: 'UploadStatus.ocrFailureStatus',
      defaultMessage: 'Completed',
      description: 'OCR failure status text',
    }),
    ALGORITHM_FAILURE: intl.formatMessage({
      id: 'UploadStatus.algorithmFailureStatus',
      defaultMessage: 'Completed',
      description: 'Algorithm failure status text',
    }),
    DUPLICATE: intl.formatMessage({
      id: 'UploadStatus.duplicateStatus',
      defaultMessage: 'Duplicate',
      description: 'Duplicate status text',
    }),
  };

  function renderAlgoStatusModal() {
    if (modal) {
      return (
        <EcModal
          modalType={MODAL_SIMPLE}
          title={intl.formatMessage({
            id: 'UploadStatus.algorithmStatusTitle',
            defaultMessage: 'Algorithm Status',
            description: 'Title for algorithm status modal',
          })}
          width="560px"
          hideModal={() => showModal(false)}
          includeCancelButton={false}
          hideCloseIcon={true}
          modalButtons={
            <Button
              variant="primary"
              text={intl.formatMessage({
                id: 'UploadStatus.closeButton',
                defaultMessage: 'Close',
                description: 'Close button text',
              })}
              onClick={() => showModal(false)}
            />
          }
        >
          {renderAlgoStatus()}
        </EcModal>
      );
    }
    return null;
  }

  function renderStatusIcon() {
    switch (status) {
      case UploadStatusType.Completed:
        return <CheckmarkIcon size="20" green />;
      case UploadStatusType.Queued:
        return <QueueIcon color={black2} />;
      case UploadStatusType.Processing:
        return <SyncIcon size="20" color={evisortBlue} />;
      case UploadStatusType.ProcessingError:
      case UploadStatusType.UploadFailed:
      case UploadStatusType.UploadFileTypeFailure:
        return <CloseIcon size="20" color={evisortRed} />;
      case UploadStatusType.Uploading:
        return <UploadArrowUpIcon size="20" color={evisortYellow} />;
      case UploadStatusType.Duplicate:
        return featureFlagIncluded(user, 'DUPLICATE_STATUS') ? (
          <WarningIcon size="20" color={evisortOrange} />
        ) : (
          <CheckmarkIcon size="20" green />
        );
      case UploadStatusType.OcrFailure:
      case UploadStatusType.AlgorithmFailure:
        return <WarningIcon size="20" yellow />;
      default:
        break;
    }
  }

  function renderAlgoStatus() {
    const algoList = algorithmStatusHelper(algoStatus);

    return algoList
      ? algoList.map((item) => {
          let itemMessageLabel = EM_DASH;
          if (item.message.length) itemMessageLabel = item.message;

          return (
            <div key={item.name} className={classes.algoContainer}>
              <span className={classes.statusContainer}>
                <span
                  className={
                    item.status === 1
                      ? classes.algoStatusGreenDot
                      : item.status === 0
                        ? classes.algoStatusYellowDot
                        : item.status === -2
                          ? classes.algoStatusBlueDot
                          : classes.algoStatusRedDot
                  }
                />
                <span className={classes.algoName}>{item.name}</span>
              </span>
              {item.status === 1 ? (
                <div className={classes.statusMessage}>
                  {intl.formatMessage(
                    {
                      id: 'UploadStatus.algorithmMessage',
                      defaultMessage: 'Message {message}',
                      description:
                        'Algorithm status message. Example: message = "Processing complete"',
                    },
                    { message: itemMessageLabel },
                  )}
                </div>
              ) : item.status === 0 ? (
                <div className={classes.statusMessage}>
                  {intl.formatMessage({
                    id: 'UploadStatus.algorithmInProgress',
                    defaultMessage: 'In Progress',
                    description: 'Algorithm in progress status',
                  })}
                </div>
              ) : item.status === -2 ? (
                <div className={classes.statusMessage}>
                  {intl.formatMessage({
                    id: 'UploadStatus.algorithmPartiallyComplete',
                    defaultMessage: 'Partially Complete',
                    description: 'Algorithm partially complete status',
                  })}
                </div>
              ) : (
                <div className={classes.statusErrorMessage}>
                  {intl.formatMessage({
                    id: 'UploadStatus.algorithmFailed',
                    defaultMessage: 'Failed',
                    description: 'Algorithm failed status',
                  })}
                </div>
              )}
            </div>
          );
        })
      : null;
  }

  const isSuperAdmin = testIsSuperAdmin(user);

  return (
    <div className={classes.statusContainer}>
      <div
        className={classes.uploadStatusTooltip}
        data-tip
        data-for={`uploadStatusTooltip-${logId}`}
      >
        <Tooltip
          id={`uploadStatusTooltip-${logId}`}
          placement="left"
          tooltip={
            status !== UploadStatusType.Duplicate
              ? UploadStatusTooltip[status]
              : undefined
          }
        >
          {renderStatusIcon()}
          <span className={classes.statusName}>
            {featureFlagIncluded(user, 'DUPLICATE_STATUS')
              ? UploadStatuses[status]
              : status === UploadStatusType.Duplicate
                ? UploadStatuses[UploadStatusType.Completed]
                : UploadStatuses[status]}
            &nbsp;
          </span>
        </Tooltip>
      </div>
      {isSuperAdmin && algoStatus && (
        <button
          className={classes.algoStatusButton}
          onClick={() => showModal(true)}
        >
          <InfoIcon color={black2} size="18" />
        </button>
      )}
      {renderAlgoStatusModal()}
      {progress ? (
        <span className={classes.statusProgress}>{`\u2013 ${Math.floor(
          progress,
        )}%`}</span>
      ) : null}
    </div>
  );
}

export default injectSheet(styles)(UploadStatus);
